import { api } from './api'

/**
 * Descarcă un fișier binar (orice tip) de la un endpoint autentificat.
 * Numele vine din header-ul Content-Disposition, altfel din fallbackName.
 * Spre deosebire de `downloadXlsx`, nu validează tipul — bun pentru atașamente
 * eterogene (PDF, imagini, XLSX).
 */
export async function downloadFile(
  url: string,
  params: Record<string, unknown> = {},
  fallbackName = 'fisier',
): Promise<void> {
  const response = await api.get(url, { params, responseType: 'blob', timeout: 60_000 })
  const blob = response.data as Blob

  // Eroare JSON venită ca blob (backend a răspuns cu eroare, nu cu fișier).
  if (blob.type.includes('application/json')) {
    const text = await blob.text()
    try {
      const err = JSON.parse(text) as { error?: { message?: string } }
      throw new Error(err.error?.message ?? text)
    } catch {
      throw new Error(text || 'Descărcare invalidă')
    }
  }

  let filename = fallbackName
  const cd = response.headers['content-disposition']
  if (cd) {
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)
    if (match) filename = decodeURIComponent(match[1])
  }

  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(downloadUrl)
}

/**
 * Deschide un PDF autentificat într-o filă nouă, pentru vizualizare.
 *
 * Alexandru: „aș vrea să adaugi link de descărcare/vizualizare PV". Un `<a href>` obișnuit n-ar
 * merge: autentificarea se face prin antetul `Authorization`, care nu călătorește cu o navigare
 * pornită de browser — linkul ar primi 401 și omul ar vedea o pagină de eroare, fără să înțeleagă
 * de ce documentul lui „nu există".
 *
 * Deci se aduce fișierul cu antetul pus, se face un `blob:` local și se deschide acela. Vizualizatorul
 * browserului are apoi butoanele lui de descărcare și tipărire, deci un singur link acoperă și
 * vizualizarea, și salvarea.
 *
 * Adresa `blob:` se elibereaza dupa un minut: prea devreme si fila nou deschisa ramane goala, prea
 * tarziu si tinem in memorie documente pe care nimeni nu le mai citeste.
 */
export async function deschideFisier(url: string): Promise<void> {
  const response = await api.get(url, { responseType: 'blob', timeout: 60_000 })
  const blob = response.data as Blob

  if (blob.type.includes('application/json')) {
    const text = await blob.text()
    try {
      const err = JSON.parse(text) as { error?: { message?: string } }
      throw new Error(err.error?.message ?? text)
    } catch {
      throw new Error(text)
    }
  }

  const adresa = URL.createObjectURL(blob)
  const fila = window.open(adresa, '_blank', 'noopener')
  if (!fila) {
    // Blocat de browser. Mai bine descarcat decat pierdut fara explicatie.
    const a = document.createElement('a')
    a.href = adresa
    a.download = 'document.pdf'
    a.click()
  }
  setTimeout(() => URL.revokeObjectURL(adresa), 60_000)
}
